import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { RegionApiFrom } from '@api-gen/models/region-api-from';
import { CountryCodeApiRoaming } from '@api-roaming/models/country-code-api-roaming';
import { GetConfcodeInfoResponseApiRoaming } from '@api-roaming/models/get-confcode-info-response-api-roaming';
import { ListCommunicChannelResponseApiRoaming } from '@api-roaming/models/list-communic-channel-response-api-roaming';
import { RegEmailRequestApiRoaming } from '@api-roaming/models/reg-email-request-api-roaming';
import { RegistrationRequestApiRoaming } from '@api-roaming/models/registration-request-api-roaming';
import { ErrorResponse } from '@api/models';
import { RegisterFormComponent } from '@auth/components/register-form/register-form.component';
import { SettingsAsObject } from '@core/models/settings.model';
import { StartWithUnit } from '@core/operators/start-with-unit';
import { RouteEntity } from '@core/router/router.entity';
import { AppState } from '@core/state/app.state';
import { UserState } from '@core/state/user.state';
import { TranslateModule } from '@ngx-translate/core';
import { RxEffects } from '@rx-angular/state/effects';
import { SnackBarType } from '@shared/components/snack-bar/snack-bar.component';
import { SnackBarService } from '@shared/components/snack-bar/snack-bar.service';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';


/**
 * Registration page
 */
@Component({
    selector: 'app-register-page',
    templateUrl: './register-page.component.html',
    styleUrls: ['./register-page.component.scss'],
    providers: [RxEffects],
    standalone: true,
    imports: [
        RouterLink,
        TranslateModule,
        MatIconModule,
        AsyncPipe,
        RegisterFormComponent,
    ],
})
export class RegisterPageComponent {
    readonly #appState: AppState = inject(AppState);
    readonly #userState: UserState = inject(UserState);
    readonly #effects: RxEffects = inject(RxEffects);
    readonly #push: SnackBarService = inject(SnackBarService);

    public readonly pending$: Observable<boolean> = this.#userState.loading$.pipe(
        map((loading: Record<StartWithUnit, boolean>) => loading?.[StartWithUnit.SignUp]),
    );

    public readonly error$: Observable<ErrorResponse> = this.#userState.errors$.pipe(
        map((errors: Record<StartWithUnit, ErrorResponse>) => errors?.[StartWithUnit.SignUp]),
    );

    public readonly loggedIn$: Observable<boolean> = this.#userState.isAuthorized$;
    public readonly countryCodes$: Observable<CountryCodeApiRoaming[]> = this.#appState.countryCodes$;
    public readonly defaultCode$: Observable<CountryCodeApiRoaming> = this.#appState.defaultCountryCode$;
    public readonly regions$: Observable<RegionApiFrom[]> = this.#appState.regions$;
    public readonly settings$: Observable<SettingsAsObject> = this.#appState.settings$;

    public readonly communicationChannels$: Observable<ListCommunicChannelResponseApiRoaming[]> = this.#appState.communicationChannels$;
    public readonly communicationChannelsText$: Observable<GetConfcodeInfoResponseApiRoaming> = this.#appState.communicationChannelsInfo$;

    public readonly routeEntity: typeof RouteEntity = RouteEntity;

    constructor() {
        this.#effects.register(
            this.loggedIn$.pipe(filter(Boolean)),
            () => this.#userState.logout(),
        );
    }

    public onSubmit(data: RegistrationRequestApiRoaming | RegEmailRequestApiRoaming): void {
        if (data?.phone) {
            this.#userState.registrationByPhone(data);
        } else {
            this.#userState.registrationByEmail(data);
        }
    }

    public handlerErrorRecaptcha(): void {
        this.#push.open({
            text: 'Error recaptcha token generate',
            type: SnackBarType.Error,
        });
    }
}
